import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import type { User, Session } from '@supabase/supabase-js';

// Null auth state for initial render (before auth is loaded)
const nullAuthState = {
  user: null as User | null,
  session: null as Session | null,
  isAdmin: false,
  isLoading: true,
  signIn: async () => ({ error: new Error('Auth not loaded yet') }),
  signUp: async () => ({ error: new Error('Auth not loaded yet') }),
  signOut: async () => {},
};

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isAdmin: boolean;
  isLoading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
}

// Export the context so AuthContext.tsx can use it
export const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Deferred wrapper that delays auth initialization until after first paint
export function DeferredAuthProvider({ children }: { children: ReactNode }) {
  const [shouldLoadAuth, setShouldLoadAuth] = useState(false);
  const [AuthProviderComponent, setAuthProviderComponent] = useState<React.ComponentType<{ children: ReactNode }> | null>(null);

  useEffect(() => {
    const loadAuth = () => {
      import('./AuthContext').then(module => {
        setAuthProviderComponent(() => module.AuthProvider);
        setShouldLoadAuth(true);
      });
    };

    const scheduleLoad = () => {
      if ('requestIdleCallback' in window) {
        const id = requestIdleCallback(loadAuth, { timeout: 5000 });
        return () => cancelIdleCallback(id);
      } else {
        // Safari fallback
        const timer = setTimeout(loadAuth, 2000);
        return () => clearTimeout(timer);
      }
    };

    // Wait for window.load FIRST, then schedule idle callback
    // This guarantees auth chunk loads after LCP completes
    if (document.readyState === 'complete') {
      return scheduleLoad();
    } else {
      let cleanup: (() => void) | undefined;
      const handleLoad = () => {
        cleanup = scheduleLoad();
      };
      window.addEventListener('load', handleLoad);
      return () => {
        window.removeEventListener('load', handleLoad);
        cleanup?.();
      };
    }
  }, []);

  // Before auth loads, provide null state so app renders immediately
  if (!shouldLoadAuth || !AuthProviderComponent) {
    return (
      <AuthContext.Provider value={nullAuthState}>
        {children}
      </AuthContext.Provider>
    );
  }

  return <AuthProviderComponent>{children}</AuthProviderComponent>;
}

// Hook that works with both deferred and regular auth
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
